import { useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { cachedLocalIP } from "@/lib/localIp";
import { describeDevice } from "@/lib/deviceInfo";

/**
 * Fires a heartbeat every 30 s while:
 *  1. the browser tab is VISIBLE (Page Visibility API), AND
 *  2. the user actually interacted (mouse / key / touch / scroll) in the last 60 s.
 *
 * Sends the *effective* active milliseconds since the previous tick — so an
 * open-but-idle tab does NOT inflate the "total active time" counter.
 * Also carries the browser's LAN IP so the Developer's Users panel can show
 * where on the yacht network each session is coming from.
 */
export function useActivityHeartbeat(enabled) {
  const lastInteractionRef = useRef(Date.now());
  const lastTickRef = useRef(Date.now());
  const IDLE_MS = 60_000;

  useEffect(() => {
    if (!enabled) return;
    const bump = () => { lastInteractionRef.current = Date.now(); };
    const evts = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    evts.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    return () => evts.forEach((e) => window.removeEventListener(e, bump));
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    // Reset the "since last tick" window when the tab goes background so we
    // don't count the entire idle stretch when it comes back.
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        lastTickRef.current = Date.now();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const tick = async () => {
      const now = Date.now();
      const delta = now - lastTickRef.current;
      lastTickRef.current = now;
      const isVisible = document.visibilityState === "visible";
      const isActive = (now - lastInteractionRef.current) < IDLE_MS;
      const activeMs = (isVisible && isActive) ? Math.min(delta, 60_000) : 0;
      try {
        const [local_ip, dev] = await Promise.all([cachedLocalIP(), describeDevice()]);
        await api.post("/auth/heartbeat", { active_ms: activeMs, local_ip, device_family: dev.label });
      } catch {
        // Silent — heartbeat failures should never leak into the UI
      }
    };
    const id = setInterval(tick, 30_000);
    // Fire one immediately so `last_active_at` refreshes on tab open too.
    tick();
    return () => clearInterval(id);
  }, [enabled]);
}
